import {
  useState,
  useRef,
  useEffect,
  useCallback,
  cloneElement,
  isValidElement,
} from "react";
import styles from "./Dropdown.module.css";
import { DropdownProps } from "./types";

/** hover 互斥组：同组仅保留一个展开实例 */
const DROPDOWN_HOVER_EXCLUSIVE_EVENT = "eweadn:dropdown-hover-exclusive";

export function Dropdown({
  children,
  header,
  options,
  onSelect,
  value,
  trigger = "hover",
  delay = 200,
  className = "",
  menuClassName = "",
  disabled = false,
  placement = "bottom",
  autoFlip = true,
  onPlacementChange,
  align = "center",
  matchTriggerWidth = false,
  onOpenChange,
  exclusiveHoverGroup,
  uiWorkshopSlot,
}: DropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [arrowLeft, setArrowLeft] = useState<number | null>(null);
  const [menuWidth, setMenuWidth] = useState<number | undefined>();
  const [resolvedPlacement, setResolvedPlacement] = useState<"bottom" | "top">(
    placement
  );
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const timerRef = useRef<number | null>(null);
  const hideTimerRef = useRef<number | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const instanceIdRef = useRef(
    `dropdown-${Math.random().toString(36).slice(2, 10)}`
  );

  const isHover = trigger === "hover";
  const isClick = trigger === "click";

  const handleOptionClick = (key: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!disabled) {
      onSelect(key);
      setIsOpen(false);
      setIsVisible(false);
    }
  };

  const getTriggerRect = () =>
    triggerRef.current?.getBoundingClientRect() ??
    containerRef.current?.getBoundingClientRect();

  const computeArrowPosition = () => {
    const triggerRect = getTriggerRect();
    const menuEl = dropdownRef.current;
    if (!triggerRect || !menuEl) return;

    const menuRect = menuEl.getBoundingClientRect();
    const triggerCenterX = triggerRect.left + triggerRect.width / 2;
    setArrowLeft(triggerCenterX - menuRect.left);
  };

  const estimateMenuHeight = () => options.length * 42 + 16 + (header ? 32 : 0);

  const resolvePlacement = (menuHeight: number): "bottom" | "top" => {
    if (!autoFlip) return placement;
    const rect = getTriggerRect();
    if (!rect) return placement;

    const gap = 12;
    const viewportPadding = 8;
    const spaceBelow = window.innerHeight - rect.bottom - gap - viewportPadding;
    const spaceAbove = rect.top - gap - viewportPadding;

    if (placement === "bottom") {
      if (spaceBelow < menuHeight) {
        if (spaceAbove >= menuHeight || spaceAbove > spaceBelow) {
          return "top";
        }
      }
      return "bottom";
    }

    if (spaceAbove < menuHeight) {
      if (spaceBelow >= menuHeight || spaceBelow > spaceAbove) {
        return "bottom";
      }
    }
    return "top";
  };

  const applyResolvedPlacement = (nextPlacement: "bottom" | "top") => {
    setResolvedPlacement((prev) => {
      if (prev !== nextPlacement) {
        onPlacementChange?.(nextPlacement);
      }
      return nextPlacement;
    });
  };

  const updatePosition = () => {
    const rect = getTriggerRect();
    if (rect) {
      const gap = 12;
      const menuHeight =
        dropdownRef.current?.offsetHeight || estimateMenuHeight();
      const nextPlacement = resolvePlacement(menuHeight);
      applyResolvedPlacement(nextPlacement);

      const leftX =
        align === "left"
          ? rect.left
          : align === "right"
            ? rect.right
            : rect.left + rect.width / 2;
      if (matchTriggerWidth) {
        setMenuWidth(rect.width);
      }
      if (nextPlacement === "top") {
        const dropdownHeight = dropdownRef.current?.offsetHeight || menuHeight;
        let top = rect.top - dropdownHeight - gap;
        const viewportPadding = 8;
        if (top < viewportPadding) {
          top = viewportPadding;
        }
        setPosition({
          top,
          left: leftX,
        });
      } else {
        const dropdownHeight = dropdownRef.current?.offsetHeight || menuHeight;
        let top = rect.bottom + gap;
        const viewportPadding = 8;
        const maxTop = window.innerHeight - viewportPadding - dropdownHeight;
        if (top > maxTop) {
          top = Math.max(viewportPadding, maxTop);
        }
        setPosition({
          top,
          left: leftX,
        });
      }
    }
  };

  const forceHideDropdown = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    setIsVisible(false);
    setIsOpen(false);
  }, []);

  useEffect(() => {
    if (!exclusiveHoverGroup || !isHover) return;

    const onExclusiveOpen = (event: Event) => {
      const detail = (
        event as CustomEvent<{ group?: string; instanceId?: string }>
      ).detail;
      if (
        detail?.group === exclusiveHoverGroup &&
        detail.instanceId !== instanceIdRef.current
      ) {
        forceHideDropdown();
      }
    };

    window.addEventListener(
      DROPDOWN_HOVER_EXCLUSIVE_EVENT,
      onExclusiveOpen as EventListener
    );
    return () =>
      window.removeEventListener(
        DROPDOWN_HOVER_EXCLUSIVE_EVENT,
        onExclusiveOpen as EventListener
      );
  }, [exclusiveHoverGroup, isHover, forceHideDropdown]);

  const showDropdown = () => {
    if (disabled) return;
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
    if (exclusiveHoverGroup) {
      window.dispatchEvent(
        new CustomEvent(DROPDOWN_HOVER_EXCLUSIVE_EVENT, {
          detail: {
            group: exclusiveHoverGroup,
            instanceId: instanceIdRef.current,
          },
        })
      );
    }
    if (isOpen && isVisible) return;
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    updatePosition();
    setIsOpen(true);
    setIsVisible(false);
    animationFrameRef.current = requestAnimationFrame(() => {
      animationFrameRef.current = requestAnimationFrame(() => {
        setIsVisible(true);
        animationFrameRef.current = null;
      });
    });
  };

  const hideDropdown = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
    if (isHover) {
      timerRef.current = window.setTimeout(() => {
        setIsVisible(false);
        hideTimerRef.current = window.setTimeout(() => {
          setIsOpen(false);
          hideTimerRef.current = null;
        }, 200);
        timerRef.current = null;
      }, delay);
    } else {
      setIsVisible(false);
      hideTimerRef.current = window.setTimeout(() => {
        setIsOpen(false);
      }, 200);
    }
  };

  const handleTriggerClick = (e: React.MouseEvent) => {
    if (disabled) return;
    e.preventDefault();
    e.stopPropagation();
    if (isOpen) {
      hideDropdown();
    } else {
      showDropdown();
    }
  };

  useEffect(() => {
    onOpenChange?.(isOpen);
  }, [isOpen, onOpenChange]);

  useEffect(() => {
    if (!isClick) return; // 仅 click 模式需要监听外部点击
    const handleClickOutside = (event: MouseEvent) => {
      if (!isOpen) return;
      const target = event.target as Node;
      // 点击 trigger 区域或 dropdown 内部时不关闭
      if (containerRef.current?.contains(target)) return;
      if (dropdownRef.current?.contains(target)) return;
      hideDropdown();
    };
    // 使用 click 事件而不是 mousedown，避免与 button 的 click 冲突
    document.addEventListener("click", handleClickOutside, true);
    return () =>
      document.removeEventListener("click", handleClickOutside, true);
  }, [isOpen, isClick]);

  useEffect(() => {
    if (!isOpen) {
      setArrowLeft(null);
      setResolvedPlacement(placement);
      return;
    }

    updatePosition();

    const syncArrow = () => {
      updatePosition();
      computeArrowPosition();
    };

    const rafId = requestAnimationFrame(() => {
      syncArrow();
      requestAnimationFrame(syncArrow);
    });

    window.addEventListener("resize", syncArrow);
    window.addEventListener("scroll", syncArrow, true);

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener("resize", syncArrow);
      window.removeEventListener("scroll", syncArrow, true);
    };
  }, [isOpen, isVisible, placement, align, options.length, autoFlip]);

  useEffect(() => {
    if (isOpen && resolvedPlacement === "top") {
      const t = window.setTimeout(() => {
        updatePosition();
        computeArrowPosition();
      }, 0);
      return () => clearTimeout(t);
    }
  }, [isOpen, resolvedPlacement]);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
      if (animationFrameRef.current)
        cancelAnimationFrame(animationFrameRef.current);
      setIsOpen(false);
    };
  }, []);

  const triggerProps = isHover
    ? { onMouseEnter: showDropdown, onMouseLeave: hideDropdown }
    : {};

  // 在 click 模式下，给 children 添加 onClick 处理函数
  const enhancedChildren =
    isClick && isValidElement(children)
      ? cloneElement(children, {
          onClick: (e: React.MouseEvent) => {
            e.preventDefault();
            e.stopPropagation();
            // 调用原有的 onClick（如果存在）
            const originalOnClick = (children as any).props?.onClick;
            if (originalOnClick) {
              originalOnClick(e);
            }
            // 触发 Dropdown 的打开/关闭
            handleTriggerClick(e);
          },
        } as any)
      : children;

  return (
    <div
      ref={containerRef}
      className={`${styles.dropdownContainer} ${disabled ? styles.disabled : ""} ${isOpen ? styles.active : ""} ${isHover ? styles.hoverMode : ""} ${className}`}
      {...(isHover
        ? { onMouseEnter: showDropdown, onMouseLeave: hideDropdown }
        : {})}
    >
      <div
        ref={triggerRef}
        className={styles.trigger}
        {...triggerProps}
        role={isClick ? "button" : undefined}
        aria-haspopup={isClick ? "menu" : undefined}
        aria-expanded={isClick ? isOpen : undefined}
      >
        {enhancedChildren}
      </div>

      {isOpen && (
        <>
          {isHover && (
            <div
              className={styles.bridge}
              style={{
                top: `${position.top - 26}px`,
                left: `${position.left}px`,
              }}
              onMouseEnter={showDropdown}
              onMouseLeave={hideDropdown}
            />
          )}
          <div
            ref={dropdownRef}
            className={`${styles.dropdown} ${menuClassName} ${isVisible ? styles.dropdownVisible : ""} ${resolvedPlacement === "top" ? styles.dropdownTop : styles.dropdownBottom} ${align === "left" ? styles.dropdownAlignLeft : ""} ${align === "right" ? styles.dropdownAlignRight : ""}`}
            {...(uiWorkshopSlot
              ? { "data-ui-workshop-slot": uiWorkshopSlot }
              : {})}
            style={{
              top: `${position.top}px`,
              left: `${position.left}px`,
              ...(matchTriggerWidth && menuWidth
                ? {
                    width: menuWidth,
                    minWidth: menuWidth,
                    maxWidth: menuWidth,
                  }
                : {}),
            }}
            role={isClick ? "menu" : undefined}
            {...(isHover
              ? { onMouseEnter: showDropdown, onMouseLeave: hideDropdown }
              : {})}
          >
            <span
              className={styles.dropdownArrow}
              aria-hidden
              style={
                arrowLeft == null
                  ? undefined
                  : {
                      left: `${arrowLeft}px`,
                      right: "auto",
                      transform: "translateX(-50%)",
                    }
              }
            />
            {header && <div className={styles.dropdownHeader}>{header}</div>}
            {options.map((option) => {
              const isSelected = value === option.key;
              return (
                <button
                  key={option.key}
                  type="button"
                  className={`${styles.dropdownItem} ${option.disabled ? styles.disabledItem : ""} ${isSelected ? styles.selectedItem : ""}`}
                  onClick={(e) => handleOptionClick(option.key, e)}
                  disabled={option.disabled || disabled}
                  role="menuitem"
                >
                  {option.icon && (
                    <span className={styles.dropdownItemIcon}>
                      {option.icon}
                    </span>
                  )}
                  <span className={styles.dropdownItemLabel}>
                    {option.label}
                  </span>
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
